import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Navbar from '../components/Navbar';
import { User, Radio, Eye, Award, Heart, Edit3 } from 'lucide-react';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('about');

  useEffect(() => {
    if (!user) navigate('/auth');
  }, [user, navigate]);

  if (!user) return null;

  const stats = [
    { icon: <Heart className="w-5 h-5" />, label: t('followers'), value: user.followers || 0 },
    { icon: <User className="w-5 h-5" />, label: t('following'), value: user.following || 0 },
    { icon: <Radio className="w-5 h-5" />, label: t('totalStreams'), value: user.total_streams || 0 },
    { icon: <Eye className="w-5 h-5" />, label: t('totalViews'), value: user.total_views || 0 },
  ];

  const tabs = [
    { id: 'about', label: t('about') },
    { id: 'streams', label: t('streams') },
    { id: 'achievements', label: t('achievements') },
  ];

  return (
    <div data-testid="profile-page" className="min-h-screen bg-[#050108]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 pt-24 pb-12">
        {/* Profile Header */}
        <div className="glass-card rounded-3xl p-8 text-center mb-8">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#FF007F] to-[#E0115F] p-[3px] mx-auto mb-4">
            <img src={user.avatar} alt={user.display_name}
              className="w-full h-full rounded-full object-cover bg-[#0a0512]" />
          </div>
          <h2 className="font-display text-2xl font-bold mb-1">{user.display_name}</h2>
          <p className="text-slate-400 text-sm mb-1">@{user.username}</p>
          <div className="flex items-center justify-center gap-4 mt-3 text-sm">
            <span className="flex items-center gap-1 text-[#FF007F]">
              <Award className="w-4 h-4" /> {t('level')} {user.level}
            </span>
            <span className="flex items-center gap-1 text-amber-400">
              {t('tokens')}: {user.tokens}
            </span>
          </div>
          <button data-testid="edit-profile-btn"
            className="mt-4 px-6 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-all text-sm flex items-center gap-2 mx-auto">
            <Edit3 className="w-4 h-4" /> {t('editProfile')}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {stats.map((s, i) => (
            <div key={i} className="glass-card rounded-2xl p-4 text-center">
              <div className="text-[#FF007F] flex justify-center mb-2">{s.icon}</div>
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${activeTab === tab.id ? 'gradient-button' : 'bg-white/5 hover:bg-white/10'}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="glass-card rounded-3xl p-8">
          {activeTab === 'about' && (
            <div>
              <h3 className="font-semibold mb-3">{t('about')}</h3>
              <p className="text-slate-400">{user.bio || 'No bio yet. Edit your profile to add one!'}</p>
            </div>
          )}
          {activeTab === 'streams' && (
            <div className="text-center py-8">
              <Radio className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No past streams yet. Go live to start!</p>
            </div>
          )}
          {activeTab === 'achievements' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {['First Stream', 'Social Butterfly', 'Game Master', 'Gift Giver', 'Night Owl', 'Fan Favorite'].map((badge, i) => (
                <div key={i} className={`rounded-2xl p-4 text-center ${i < 2 ? 'glass-card' : 'glass-card opacity-40'}`}>
                  <div className="text-2xl mb-2">{['🎬', '🦋', '🎮', '🎁', '🌙', '⭐'][i]}</div>
                  <p className="text-sm font-medium">{badge}</p>
                  <p className="text-xs text-slate-400 mt-1">{i < 2 ? 'Unlocked' : 'Locked'}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
